import { AggregateBackfillStatus } from "@/gen_src/proto/chalk/aggregate/v1/backfill_pb";
import { Icon } from "@/components/icons/Icon";
import { compileOnlyAssertNever } from "@/utils/asserts";
import React from "react";
import { cx } from "@/utils/classnames";
import { StatusBadgeV2, type StatusBadgeV2Tone } from "@/components/interactive/StatusBadgeV2";
import { Tooltip, TooltipProps, TooltipV2, type TooltipV2Props } from "@/components/TooltipV2";
import { ScheduledQueryRunStatus } from "@/gen_src/proto/chalk/server/v1/scheduled_query_run_pb";

export enum JobStatus {
  UNSPECIFIED = 0,
  INITIALIZING = 1,
  INIT_FAILED = 2,
  SKIPPED = 3,
  QUEUED = 4,
  WORKING = 5,
  COMPLETED = 6,
  FAILED = 7,
  CANCELED = 8,
}

export const getTextColorClassNameForJobStatus = (status: JobStatus) => {
  switch (status) {
    case JobStatus.COMPLETED:
      return "text-cds-bright-green-500";
    case JobStatus.FAILED:
    case JobStatus.INIT_FAILED:
      return "text-red-500";
    case JobStatus.QUEUED:
    case JobStatus.INITIALIZING:
    case JobStatus.SKIPPED:
    case JobStatus.UNSPECIFIED:
      return "text-cds-gray-400";
    case JobStatus.WORKING:
      return "text-yellow-500";
    case JobStatus.CANCELED:
      return "text-zinc-500";
    default:
      compileOnlyAssertNever(status);
      return "text-cds-gray-400";
  }
};

export function getJobStatusText(
  status: JobStatus,
  hasErrors: boolean | undefined = false,
): string {
  if (hasErrors) {
    return "This job encountered errors";
  }
  switch (status) {
    case JobStatus.COMPLETED:
      return "This job completed successfully";
    case JobStatus.FAILED:
      return "This job failed to complete";
    case JobStatus.INIT_FAILED:
      return "This job failed to initialize";
    case JobStatus.QUEUED:
      return "This job is queued and waiting for resources";
    case JobStatus.INITIALIZING:
      return "This job is initializing";
    case JobStatus.WORKING:
      return "This job is in progress";
    case JobStatus.CANCELED:
      return "This job was manually canceled";
    case JobStatus.SKIPPED:
      return "This job was skipped because a previous job was still in progress";
    case JobStatus.UNSPECIFIED:
      return "Unknown status. This job may be in an unexpected state";
    default:
      compileOnlyAssertNever(status);
      return "Unknown status. This job may be in an unexpected state";
  }
}

export function getJobStatusTextShort(
  status: JobStatus,
  hasErrors: boolean | undefined = false,
): string {
  if (hasErrors) {
    return "Errors";
  }
  switch (status) {
    case JobStatus.COMPLETED:
      return "Completed";
    case JobStatus.FAILED:
      return "Failed";
    case JobStatus.INIT_FAILED:
      return "Failed";
    case JobStatus.QUEUED:
      return "Queued";
    case JobStatus.INITIALIZING:
      return "Initializing";
    case JobStatus.WORKING:
      return "Working";
    case JobStatus.CANCELED:
      return "Canceled";
    case JobStatus.SKIPPED:
      return "Skipped";
    case JobStatus.UNSPECIFIED:
      return "Unknown";
    default:
      compileOnlyAssertNever(status);
      return "Unknown";
  }
}

export interface JobStatusIconProps {
  className?: string;
  status: JobStatus;
  hasErrors?: boolean;
}

export const JobStatusIcon: React.FC<JobStatusIconProps> = ({ status, className, hasErrors }) => {
  const fullClassName = cx(getTextColorClassNameForJobStatus(status), className);
  const label = getJobStatusText(status, hasErrors);
  const props = {
    "aria-label": label,
    className: fullClassName,
  };
  switch (status) {
    case JobStatus.COMPLETED:
      return <Icon name="check" {...props} />;
    case JobStatus.FAILED:
    case JobStatus.INIT_FAILED:
    case JobStatus.SKIPPED:
      return <Icon name="error" {...props} />;
    case JobStatus.QUEUED:
    case JobStatus.INITIALIZING:
    case JobStatus.UNSPECIFIED:
      return <Icon name="circle" {...props} />;
    case JobStatus.WORKING:
      return <Icon name="clock" {...props} />;
    case JobStatus.CANCELED:
      return <Icon name="error" {...props} />;
    default:
      compileOnlyAssertNever(status);
      return <Icon name="circle" {...props} />;
  }
};

export interface JobStatusIconWithTooltipProps {
  status: JobStatus;
}

export const JobStatusIconWithTooltip: React.FC<
  JobStatusIconWithTooltipProps & Omit<TooltipProps, "content" | "children">
> = ({ status, className, ...tooltipProps }) => {
  return (
    <Tooltip {...tooltipProps} content={getJobStatusText(status)}>
      <div>
        <JobStatusIcon className={className} status={status} />
      </div>
    </Tooltip>
  );
};

const getJobStatusBadgeTone = (
  status: JobStatus,
  hasErrors: boolean | undefined = false,
): StatusBadgeV2Tone => {
  if (hasErrors) {
    return "red";
  }

  switch (status) {
    case JobStatus.COMPLETED:
      return "green";
    case JobStatus.FAILED:
    case JobStatus.INIT_FAILED:
      return "red";
    case JobStatus.WORKING:
      return "yellow";
    case JobStatus.QUEUED:
    case JobStatus.INITIALIZING:
      return "blue";
    case JobStatus.CANCELED:
    case JobStatus.SKIPPED:
    case JobStatus.UNSPECIFIED:
      return "neutral";
    default:
      compileOnlyAssertNever(status);
      return "neutral";
  }
};

export interface JobStatusBadgeProps {
  hasErrors?: boolean;
  status: JobStatus;
}

export const JobStatusBadge: React.FC<
  JobStatusBadgeProps & Omit<TooltipV2Props, "content" | "children">
> = ({ status, hasErrors, ...tooltipProps }) => {
  return (
    <TooltipV2 {...tooltipProps} content={getJobStatusText(status, hasErrors)}>
      <StatusBadgeV2 tone={getJobStatusBadgeTone(status, hasErrors)}>
        {getJobStatusTextShort(status, hasErrors)}
      </StatusBadgeV2>
    </TooltipV2>
  );
};

export function aggregateBackfillStatusToJobStatus(status: AggregateBackfillStatus): JobStatus {
  switch (status) {
    case AggregateBackfillStatus.UNSPECIFIED:
      return JobStatus.UNSPECIFIED;
    case AggregateBackfillStatus.INITIALIZING:
      return JobStatus.INITIALIZING;
    case AggregateBackfillStatus.INIT_FAILED:
      return JobStatus.INIT_FAILED;
    case AggregateBackfillStatus.SKIPPED:
      return JobStatus.SKIPPED;
    case AggregateBackfillStatus.QUEUED:
      return JobStatus.QUEUED;
    case AggregateBackfillStatus.WORKING:
      return JobStatus.WORKING;
    case AggregateBackfillStatus.COMPLETED:
      return JobStatus.COMPLETED;
    case AggregateBackfillStatus.FAILED:
      return JobStatus.FAILED;
    case AggregateBackfillStatus.CANCELED:
      return JobStatus.CANCELED;
    default:
      compileOnlyAssertNever(status);
      return JobStatus.UNSPECIFIED;
  }
}

export function scheduledQueryRunStatusToJobStatus(status: ScheduledQueryRunStatus): JobStatus {
  switch (status) {
    case ScheduledQueryRunStatus.UNSPECIFIED:
      return JobStatus.UNSPECIFIED;
    case ScheduledQueryRunStatus.INITIALIZING:
      return JobStatus.INITIALIZING;
    case ScheduledQueryRunStatus.INIT_FAILED:
      return JobStatus.INIT_FAILED;
    case ScheduledQueryRunStatus.SKIPPED:
      return JobStatus.SKIPPED;
    case ScheduledQueryRunStatus.QUEUED:
      return JobStatus.QUEUED;
    case ScheduledQueryRunStatus.WORKING:
      return JobStatus.WORKING;
    case ScheduledQueryRunStatus.COMPLETED:
      return JobStatus.COMPLETED;
    case ScheduledQueryRunStatus.FAILED:
      return JobStatus.FAILED;
    case ScheduledQueryRunStatus.CANCELED:
      return JobStatus.CANCELED;
    default:
      compileOnlyAssertNever(status);
      return JobStatus.UNSPECIFIED;
  }
}
